import { ImportExportRounded } from "@mui/icons-material";
import { Button, Card, CardContent, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useState } from "react";
import {
  AddExistingMetricModal,
  CreateMetricModal,
  MetricsCollectionFilterButton,
  MetricsGrid,
  ReorderMetricCollectionModal,
} from "src/components";
import { useAddMetricToCollection, useGetMetricCollection } from "src/http";
import { lexorank } from "src/utilities/lexorank";
import { useTeamPageLayoutContext } from "../TeamPageLayout";
import { useTeamMetricsSettings } from "./hooks";

const TeamMetricsPage = () => {
  const { team } = useTeamPageLayoutContext();
  const { settings, updateSettings } = useTeamMetricsSettings(team.slug);
  const { data: metrics } = useGetMetricCollection(team.metricCollectionGuid);
  const { mutate: addMetric } = useAddMetricToCollection();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isAddExistingModalVisible, setIsAddExistingModalVisible] = useState(false);
  const [isReorderModalVisible, setIsReorderModalVisible] = useState(false);

  function handleMetricAdded(metricGuid: string) {
    addMetric({
      metricCollectionGuid: team.metricCollectionGuid,
      metricGuid: metricGuid,
      sortOrder: lexorank.getNextRank(metrics),
    });
  }

  return (
    <>
      <Stack spacing={{ xs: 0.75, md: 1 }}>
        <Card>
          <CardContent>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                alignItems: "center",
              }}
            >
              <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                <Stack spacing={0.25}>
                  <Typography variant="h5">Metrics</Typography>
                  <Typography variant="body1">Metrics relevant to the team.</Typography>
                </Stack>
                {team.metricCount > 1 && (
                  <Stack direction="row" spacing={0.5}>
                    {(team.isAdministrator || team.isMember) && (
                      <Button
                        variant="tertiary"
                        startIcon={<ImportExportRounded />}
                        onClick={() => setIsReorderModalVisible(true)}
                      >
                        Reorder
                      </Button>
                    )}

                    <MetricsCollectionFilterButton
                      variant="text"
                      metricCollectionGuid={team.metricCollectionGuid}
                      filters={settings.filters}
                      onFiltersChanged={(filters) => updateSettings({ filters })}
                    />
                  </Stack>
                )}
              </Stack>

              {(team.isAdministrator || team.isMember) && (
                <Button variant="contained" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  Add Metric
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        <MetricsGrid
          metricCollectionGuid={team.metricCollectionGuid}
          filters={settings.filters}
          userCanRemove={(metric) => (team.isMember || team.isAdministrator) && metric.assignedTeam.slug !== team.slug}
        />
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setIsCreateModalVisible(true);
          }}
        >
          New
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setIsAddExistingModalVisible(true);
          }}
        >
          Existing
        </MenuItem>
      </Menu>

      <CreateMetricModal
        visible={isCreateModalVisible}
        onAdded={() => setIsCreateModalVisible(false)}
        onCancelled={() => setIsCreateModalVisible(false)}
      />

      <AddExistingMetricModal
        visible={isAddExistingModalVisible}
        onAdded={(metricGuid) => {
          handleMetricAdded(metricGuid);
          setIsAddExistingModalVisible(false);
        }}
        onCancelled={() => setIsAddExistingModalVisible(false)}
        excludedTeamSlug={team.slug}
      />

      <ReorderMetricCollectionModal
        visible={isReorderModalVisible}
        metricCollectionGuid={team.metricCollectionGuid}
        onSave={() => setIsReorderModalVisible(false)}
        onCancel={() => setIsReorderModalVisible(false)}
      />
    </>
  );
};

export { TeamMetricsPage };
